import { RedoButton } from "@redotech/arbiter/buttons/redo-button";
import { Flex } from "@redotech/arbiter/flex";
import { TextRedoListItem } from "@redotech/arbiter/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/arbiter/select-dropdown/redo-single-select-dropdown-input";
import { Text } from "@redotech/arbiter/text";
import { RedoToggle } from "@redotech/arbiter/toggle/redo-toggle";
import { DisabledTooltip } from "@redotech/arbiter/tooltip/disabled-tooltip";
import {
  isAdminCheckoutOptimizationUser,
  isTrustedAdminCheckoutOptimizationUser,
} from "@redotech/redo-conversion-core-common/checkouts/trusted-users";
import {
  CoverageMlModelId,
  INTENT_V12_28_2025_COVERAGE_ML_MODEL_ID,
  STAGECOACH_COVERAGE_ML_MODEL_ID,
} from "@redotech/redo-coverage-common/coverage-ml/types";
import { t, useTranslation } from "@redotech/redo-i18n";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import TrashIcon from "@redotech/redo-icons/trash-01.svg";
import { PricingRuleSet } from "@redotech/redo-return-coverage-common/coverage-types";
import {
  groupInput,
  GroupInput,
  Input,
  input,
  InputSchema,
  listInput,
} from "@redotech/ui/form";
import { ValidationError, Validator } from "@redotech/ui/validator";
import { numberValidator } from "@redotech/ui/validators";
import { arrayEqual, stringEqual } from "@redotech/util/equal";
import { incCache, incCompose } from "@redotech/util/inc";
import classNames from "classnames";
import round from "lodash/round";
import { memo, ReactNode, useEffect, useMemo, useState } from "react";
import * as gridCss from "../grid.module.css";
import RightArrowIcon from "../icon-old/right-arrow.svg";
import { LabeledInput, LabelTheme } from "../labeled-input";
import {
  CountryFormMultiSelectDropdown,
  FormSelectDropdown,
  SelectDropdown,
} from "../select-dropdown";
import { SettingSectionWrapper } from "../setting-wrapper/setting-section-wrapper";
import { SettingWrapper } from "../setting-wrapper/setting-wrapper";
import { Tabs } from "../tab";
import { FormTextInput } from "../text-input";
import * as pricingCss from "./pricing.module.css";

const priceBracketForm = groupInput({
  value: input<string>({ validator: numberValidator({ min: 0.01 }) }),
  pricePoint: input<string>(),
});

const priceRuleDefault = (
  index: number,
): InputSchema.Value<typeof priceBracketForm> => ({
  value: "2",
  pricePoint: "0",
});

export const ascendingValuesValidator: Validator<PricingBracketValue[]> = (
  value,
) => {
  const errors: ValidationError[] = [];

  for (let i = 0; i < value.length; i++) {
    if (i === value.length - 1) {
      break;
    }
    const current = value[i];
    const next = value[i + 1];
    if (
      current &&
      next &&
      Number(current.pricePoint) > Number(next.pricePoint)
    ) {
      errors.push(`${i}`);
    }
  }
  return errors;
};

export const percentageValidator: Validator<any> = (
  value: PricingRuleSetValue,
) => {
  const errors: ValidationError[] = [];

  if (value.type === "percentage") {
    for (let i = 0; i < value.priceBrackets.length; i++) {
      const bracket = value.priceBrackets[i];
      if (bracket && +bracket.value > 100) {
        errors.push(`${i}`);
      }
    }
  }

  return errors;
};

const pricingRuleSetSchema = groupInput(
  {
    type: input<"fixed" | "percentage">(),
    noCoverage: input<boolean | undefined>(),
    countries: input<string[]>({ equal: arrayEqual(stringEqual) }),
    priceBrackets: listInput(
      () => priceBracketForm,
      priceRuleDefault,
      (_, index) => index,
      { validator: ascendingValuesValidator },
    ),
  },
  { validator: percentageValidator },
);

export function skipNoCoverageErrors<
  Value extends { noCoverage: boolean | undefined },
  Input_ extends Input<Value>,
>(schema: InputSchema<Value, Input_>): InputSchema<Value, Input_> {
  return (initial, setValue) =>
    incCompose(
      schema(initial, setValue),
      incCache(
        (form: Input_): Input_ =>
          form.value.noCoverage ? { ...form, errors: [], allErrors: [] } : form,
      ),
    );
}

export const pricingRuleSetForm = skipNoCoverageErrors(pricingRuleSetSchema);

export type PricingRuleSetInput = InputSchema.Value<typeof pricingRuleSetForm>;

export const pricingRuleSetDefault = (index: number): PricingRuleSetInput => ({
  type: "percentage",
  noCoverage: undefined,
  countries: [],
  priceBrackets: [{ value: "2", pricePoint: "0" }],
});

export function pricingCountryErrors(rules: PricingRuleSetInput[]): string[] {
  const [domestic, ...international] = rules;
  if (
    !domestic ||
    (rules.length <= 2 && !rules.some((rule) => rule.noCoverage))
  ) {
    return [];
  }
  const countries = new Set(domestic.countries);
  let haveAllCountries = domestic.countries.length === 0;
  for (const rule of international) {
    if (!domestic.countries.length && !rule.noCoverage) {
      return [t("Select domestic countries before adding international rates")];
    }
    if (!rule.countries.length) {
      if (haveAllCountries) {
        return [t("Only one country group can apply everywhere else")];
      }
      haveAllCountries = true;
    }
    for (const country of rule.countries) {
      if (countries.has(country)) {
        return [t("A country can only belong to one pricing group")];
      }
      countries.add(country);
    }
  }
  return [];
}

export const minMaxValidator: Validator<any> = (value: PricingFormValue) => {
  const errors: ValidationError[] = [];
  for (let i = 0; i < value.pricingRuleSet.length; i++) {
    const ruleSet = value.pricingRuleSet[i];
    if (!ruleSet || ruleSet.noCoverage || ruleSet.type === "percentage") {
      continue;
    }

    for (let j = 0; j < ruleSet.priceBrackets.length; j++) {
      const bracket = ruleSet.priceBrackets[j];
      if (!bracket?.value.length) {
        continue;
      }

      if (
        Number(bracket.value) < Number(value.minPrice) ||
        Number(bracket.value) > Number(value.maxPrice)
      ) {
        errors.push(`${i},${j}`);
      }
    }
  }
  return errors;
};

export const pricingFormValues = {
  minPrice: input<string>({ validator: numberValidator({ min: 0.01 }) }),
  maxPrice: input<string>({ validator: numberValidator({ min: 0.01 }) }),
  percentage: input<string>({
    validator: numberValidator({ min: 0.01, max: 100 }),
  }),
  internationalPricingEnabled: input<boolean>(),
  pricingRuleSet: listInput(
    () => pricingRuleSetForm,
    pricingRuleSetDefault,
    (_, index) => index,
  ),
};

export const pricingForm = groupInput(pricingFormValues, {
  validator: minMaxValidator,
});

export const pricingDefault: InputSchema.Value<typeof pricingForm> = {
  minPrice: "1",
  maxPrice: "15",
  percentage: "2",
  internationalPricingEnabled: false,
  pricingRuleSet: [
    {
      type: "percentage",
      noCoverage: undefined,
      countries: [],
      priceBrackets: [{ value: "2", pricePoint: "0" }],
    },
  ],
};

export type PricingForm = InputSchema.Input<typeof pricingForm>;
export type PricingRuleSetForm = InputSchema.Input<typeof pricingRuleSetForm>;
type PricingFormValue = InputSchema.Value<typeof pricingForm>;
type PricingRuleSetValue = InputSchema.Value<typeof pricingRuleSetForm>;
type PricingBracketValue = InputSchema.Value<typeof priceBracketForm>;

/** Appends a tier one unit above the last, $100 further up the cart. */
export function addPriceTier(pricingRuleSet: PricingRuleSetForm): void {
  const brackets = pricingRuleSet.inputs.priceBrackets.value;
  const last = brackets.at(-1);
  pricingRuleSet.inputs.priceBrackets.setValue([
    ...brackets,
    {
      value: String(Number(last?.value ?? 0) + 1),
      pricePoint: String(Number(last?.pricePoint ?? 0) + 100),
    },
  ]);
}

/** Merges tier `index` into the next, which keeps its start and takes this tier's price. */
export function removePriceTier(
  pricingRuleSet: PricingRuleSetForm,
  index: number,
): void {
  const brackets = pricingRuleSet.inputs.priceBrackets.value;
  const next = brackets[index + 1];
  if (!next) {
    return;
  }
  pricingRuleSet.inputs.priceBrackets.setValue(
    brackets.flatMap((bracket, i) => {
      if (i === index) {
        return [{ ...bracket, value: next.value }];
      }
      return i === index + 1 ? [] : [bracket];
    }),
  );
}

function deletePriceTier(pricingRuleSet: PricingRuleSetForm, index: number) {
  const brackets = pricingRuleSet.inputs.priceBrackets.value;
  if (index === brackets.length - 1) {
    pricingRuleSet.inputs.priceBrackets.setValue(brackets.slice(0, -1));
  } else {
    removePriceTier(pricingRuleSet, index);
  }
}

interface CartTotalBounds {
  min: number | null | undefined;
  max: number | null | undefined;
}

export function priceTierErrors({
  pricingRuleSet,
  ruleSetIndex,
  pricingErrors,
  tierIndex,
}: {
  pricingRuleSet: PricingRuleSetForm;
  ruleSetIndex: number;
  /** The pricing group's own errors, where the min/max check reports `rule,tier`. */
  pricingErrors: readonly string[];
  tierIndex: number;
}): {
  outsideMinMax: boolean;
  overHundredPercent: boolean;
  notAscending: boolean;
} {
  return {
    outsideMinMax: pricingErrors.includes(`${ruleSetIndex},${tierIndex}`),
    overHundredPercent: pricingRuleSet.errors.includes(`${tierIndex}`),
    notAscending: pricingRuleSet.inputs.priceBrackets.errors.includes(
      `${tierIndex + 1}`,
    ),
  };
}

export const PriceBracketControls = memo(function PriceBracketControls({
  pricingRuleSet,
  minMaxErrors,
  index,
  canEditSettings = true,
  adminApp = false,
  cartTotalBounds,
  currencyDisplay,
}: {
  pricingRuleSet: PricingRuleSetForm;
  minMaxErrors: string[];
  index: number;
  canEditSettings: boolean;
  adminApp?: boolean;
  cartTotalBounds?: CartTotalBounds;
  currencyDisplay?: string;
}) {
  const { t } = useTranslation();
  const priceBrackets = pricingRuleSet.inputs.priceBrackets;
  const cartMin = cartTotalBounds?.min ?? undefined;
  const cartMax = cartTotalBounds?.max ?? undefined;
  const elements = [];
  let isLast = false;

  for (let i = 0; i < priceBrackets.inputs.length; i++) {
    const currentBracket = priceBrackets.inputs[i];
    const nextBracket = priceBrackets.inputs[i + 1];
    if (!currentBracket) {
      continue;
    }
    const fromValue = currentBracket.value.pricePoint;
    let toInput;

    if (i === priceBrackets.inputs.length - 1) {
      toInput = currentBracket.inputs.pricePoint;
      isLast = true;
    } else if (!nextBracket) {
      continue;
    } else {
      toInput = nextBracket.inputs.pricePoint;
    }

    const aboveCartMax =
      !isLast && cartMax !== undefined && Number(toInput.value) >= cartMax;
    const {
      outsideMinMax: minMaxError,
      overHundredPercent: highPercentError,
      notAscending: priceBracketError,
    } = priceTierErrors({
      pricingRuleSet,
      ruleSetIndex: index,
      pricingErrors: minMaxErrors,
      tierIndex: i,
    });

    elements.push(
      // design-allow: pre-existing settings grid
      <div className={gridCss.grid} key={i}>
        <span
          className={classNames(
            pricingCss.monospace,
            gridCss.span3,
            pricingCss.centered,
          )}
        >{`$ ${
          i === 0
            ? Number(cartMin ?? fromValue).toFixed(2)
            : (Number(fromValue) + 0.01).toFixed(2)
        }`}</span>

        <span
          className={classNames(
            pricingCss.monospace,
            gridCss.span1,
            pricingCss.centered,
          )}
        >
          -
        </span>
        {isLast && (
          <span
            className={classNames(
              pricingCss.monospace,
              gridCss.span3,
              pricingCss.centered,
            )}
          >
            {cartMax === undefined ? t("No limit") : `$ ${cartMax.toFixed(2)}`}
          </span>
        )}
        {!isLast && (
          // design-allow: pre-existing settings grid
          <div
            className={classNames(
              pricingCss.monospace,
              gridCss.span3,
              pricingCss.errorOutline,
            )}
          >
            <FormTextInput
              description={
                priceBracketError ? (
                  <Text fontSize="xs" textColor="error">
                    {t("Must be less than next row")}
                  </Text>
                ) : aboveCartMax ? (
                  <Text fontSize="xs" textColor="error">
                    {t("Must be below the cart total maximum")}
                  </Text>
                ) : (
                  ""
                )
              }
              errorOverride={priceBracketError || aboveCartMax}
              input={toInput}
              label=""
              labelTheme={LabelTheme.THIN}
              min={0.01}
              prefix={currencyDisplay}
              readonly={!canEditSettings}
              step={0.01}
            />
          </div>
        )}

        <RightArrowIcon
          className={classNames(pricingCss.icon, pricingCss.centered)}
        />

        {/* design-allow: pre-existing settings grid */}
        <div className={classNames(pricingCss.monospace, gridCss.span3)}>
          <FormTextInput
            description={
              minMaxError ? (
                <Text fontSize="xs" textColor="error">
                  Value is outside of {adminApp ? <>your</> : <>the</>} min or
                  max
                </Text>
              ) : highPercentError ? (
                <Text fontSize="xs" textColor="error">
                  Percentage can't exceed 100
                </Text>
              ) : (
                ""
              )
            }
            errorOverride={minMaxError || highPercentError}
            input={currentBracket.inputs.value}
            label=""
            labelTheme={LabelTheme.THIN}
            max={100}
            min={0.01}
            prefix={
              pricingRuleSet.value.type === "percentage" ? "" : currencyDisplay
            }
            readonly={!canEditSettings}
            step={0.01}
            suffix={pricingRuleSet.value.type === "percentage" ? "%" : ""}
          />
        </div>

        {canEditSettings && priceBrackets.inputs.length > 1 && (
          <RedoButton
            aria-label={t("Remove price tier")}
            hierarchy="tertiary"
            IconLeading={TrashIcon}
            onClick={(event) => {
              deletePriceTier(pricingRuleSet, i);
              event.stopPropagation();
            }}
            size="md"
          />
        )}
      </div>,
    );
  }

  return (
    <div>
      <div className={gridCss.grid}>
        <span className={classNames(gridCss.span3, pricingCss.centered)}>
          From
        </span>
        <span className={classNames(gridCss.span5, pricingCss.centered)}>
          To
        </span>
        <div className={gridCss.span4}>
          <FormSelectDropdown
            description=""
            disabled={!canEditSettings}
            input={pricingRuleSet.inputs.type}
            label=""
            options={["percentage", "fixed"]}
            placeholder="Default"
          >
            {pricingTypeLabel}
          </FormSelectDropdown>
        </div>
      </div>

      <Flex dir="column" gap="md" pt="md">
        {elements}
      </Flex>

      {canEditSettings && (
        <Flex justify="flex-end" pt="md">
          <RedoButton
            hierarchy="secondary"
            IconLeading={PlusIcon}
            onClick={() => addPriceTier(pricingRuleSet)}
            size="sm"
            text={t("Add new pricing tier")}
          />
        </Flex>
      )}
    </div>
  );
});

export const CountryCoverageSelect = memo(function CountryCoverageSelect({
  input,
  disabled = false,
}: {
  input: Input<boolean | undefined>;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <SelectDropdown
      disabled={disabled}
      options={[false, true]}
      value={input.value === true}
      valueChange={(value) => input.setValue(value || undefined)}
    >
      {(noCoverage) => (noCoverage ? t("Exclude") : t("Offer"))}
    </SelectDropdown>
  );
});

/** Keeps international rule sets while international pricing is on. */
export function useInternationalPricingRuleSet(
  {
    inputs: { pricingRuleSet, percentage, internationalPricingEnabled },
  }: {
    inputs: Pick<
      PricingForm["inputs"],
      "pricingRuleSet" | "percentage" | "internationalPricingEnabled"
    >;
  },
  countryGroups = false,
): void {
  useEffect(() => {
    if (
      (countryGroups
        ? pricingRuleSet.value.length > 1
        : pricingRuleSet.value.length === 2) &&
      !internationalPricingEnabled.value
    ) {
      pricingRuleSet.setValue(pricingRuleSet.value.slice(0, 1));
    } else if (
      internationalPricingEnabled.value &&
      pricingRuleSet.value.length === 1
    ) {
      pricingRuleSet.setValue([
        ...pricingRuleSet.value,
        {
          type: "percentage",
          noCoverage: undefined,
          countries: [],
          priceBrackets: [
            {
              value: percentage.value
                ? String(round(Number(percentage.value), 3))
                : "2",
              pricePoint: "0",
            },
          ],
        },
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryGroups, internationalPricingEnabled.value]);
}

const CountryGroupCard = memo(function CountryGroupCard({
  title,
  action,
  children,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Flex
      borderColor="secondary"
      borderRadius="lg"
      borderStyle="solid"
      borderWidth="1px"
      dir="column"
      gap="md"
      p="lg"
      w="full"
    >
      <Flex align="center" justify="space-between">
        <Text fontSize="sm" fontWeight="medium">
          {title}
        </Text>
        {action}
      </Flex>
      {children}
    </Flex>
  );
});

export const Pricing = memo(function Pricing({
  input,
  canEditSettings,
  adminApp = false,
  cartTotalBounds,
  countryGroups = false,
  currencyDisplay,
  minPriceFooter,
  maxPriceFooter,
  maxPriceInput,
  maxPriceDisabled,
}: {
  input: PricingForm;
  canEditSettings: boolean;
  adminApp?: boolean;
  cartTotalBounds?: CartTotalBounds;
  countryGroups?: boolean;
  currencyDisplay?: string;
  minPriceFooter?: ReactNode;
  maxPriceFooter?: ReactNode;
  maxPriceInput: Input<string>;
  maxPriceDisabled?: boolean;
}) {
  const { t } = useTranslation();
  const { minPrice, pricingRuleSet, internationalPricingEnabled } =
    input.inputs;
  const domesticRuleSet = pricingRuleSet.inputs[0];
  useInternationalPricingRuleSet(input, countryGroups);

  const [property, setProperty] = useState(CountrySet.DOMESTIC);

  return (
    <div className={gridCss.grid}>
      <MinMaxPriceInput
        canEditSettings={canEditSettings}
        currencyDisplay={currencyDisplay}
        maxPriceDisabled={maxPriceDisabled}
        maxPriceFooter={maxPriceFooter}
        maxPriceInput={maxPriceInput}
        minPriceFooter={minPriceFooter}
        minPriceInput={minPrice}
      />

      <div className={classNames(gridCss.span12)}>
        <Tabs<CountrySet>
          keyFn={(option) => option}
          options={[CountrySet.DOMESTIC, CountrySet.INTERNATIONAL]}
          tab={(option) => CountrySetLables[option]}
          value={property}
          valueChange={setProperty}
        >
          {property === CountrySet.DOMESTIC ? (
            <div className={classNames(gridCss.span12)}>
              {domesticRuleSet && (
                <CountryGroupCard
                  title={`Select ${adminApp ? "" : "your "}domestic countries`}
                >
                  <SettingWrapper compact id="domestic-countries">
                    <CountryFormMultiSelectDropdown
                      disabled={!canEditSettings}
                      emptyLabel="All"
                      input={domesticRuleSet.inputs.countries}
                      label=""
                    />
                  </SettingWrapper>
                  <SettingWrapper compact id="price-brackets">
                    <PriceBracketControls
                      adminApp={adminApp}
                      canEditSettings={canEditSettings}
                      cartTotalBounds={cartTotalBounds}
                      currencyDisplay={currencyDisplay}
                      index={0}
                      minMaxErrors={input.errors}
                      pricingRuleSet={domesticRuleSet}
                    />
                  </SettingWrapper>
                </CountryGroupCard>
              )}
            </div>
          ) : (
            <Flex dir="column" gap="lg" w="full">
              {countryGroups &&
                pricingCountryErrors(input.value.pricingRuleSet).map(
                  (error) => (
                    <Text fontSize="sm" key={error} textColor="error">
                      {error}
                    </Text>
                  ),
                )}
              <SettingWrapper compact id="international-pricing">
                <RedoToggle
                  description={
                    countryGroups
                      ? t(
                          "Set coverage and pricing for international country groups",
                        )
                      : `Enables coverage for all countries outside of ${
                          adminApp ? "the merchant's" : "your"
                        } domestic countries`
                  }
                  disabled={!canEditSettings}
                  label="Enable coverage internationally"
                  setValue={internationalPricingEnabled.setValue}
                  size="sm"
                  value={internationalPricingEnabled.value}
                />
              </SettingWrapper>
              {pricingRuleSet.inputs.length > 1 &&
                internationalPricingEnabled.value &&
                pricingRuleSet.inputs
                  .slice(1, countryGroups ? undefined : 2)
                  .map((ruleSet, index) => (
                    <CountryGroupCard
                      action={
                        adminApp &&
                        countryGroups && (
                          <RedoButton
                            aria-label={t("Remove country group")}
                            disabled={!canEditSettings}
                            hierarchy="tertiary"
                            IconLeading={TrashIcon}
                            onClick={() => {
                              const next = pricingRuleSet.value.filter(
                                (_, ruleIndex) => ruleIndex !== index + 1,
                              );
                              pricingRuleSet.setValue(next);
                              if (next.length === 1) {
                                internationalPricingEnabled.setValue(false);
                              }
                            }}
                            size="md"
                          />
                        )
                      }
                      key={index}
                      title={t("Countries")}
                    >
                      {countryGroups && (
                        <>
                          <CountryFormMultiSelectDropdown
                            disabled={!canEditSettings || !adminApp}
                            emptyLabel={t("Everywhere else")}
                            input={ruleSet.inputs.countries}
                            label=""
                          />
                          <LabeledInput label={t("Coverage")}>
                            <CountryCoverageSelect
                              disabled={!canEditSettings || !adminApp}
                              input={ruleSet.inputs.noCoverage}
                            />
                          </LabeledInput>
                        </>
                      )}
                      {!ruleSet.value.noCoverage && (
                        <PriceBracketControls
                          adminApp={adminApp}
                          canEditSettings={canEditSettings}
                          cartTotalBounds={cartTotalBounds}
                          currencyDisplay={currencyDisplay}
                          index={index + 1}
                          minMaxErrors={input.errors}
                          pricingRuleSet={ruleSet}
                        />
                      )}
                    </CountryGroupCard>
                  ))}
              {adminApp &&
                countryGroups &&
                internationalPricingEnabled.value && (
                  <RedoButton
                    disabled={!canEditSettings}
                    hierarchy="secondary"
                    IconLeading={PlusIcon}
                    onClick={() => {
                      const domestic = pricingRuleSet.value[0];
                      if (!domestic) {
                        return;
                      }
                      const international = pricingRuleSet.value.slice(1);
                      pricingRuleSet.setValue([
                        domestic,
                        ...international.filter(
                          (rule) => rule.countries.length,
                        ),
                        pricingRuleSetDefault(0),
                        ...international.filter(
                          (rule) => !rule.countries.length,
                        ),
                      ]);
                    }}
                    size="md"
                    text={t("Add international countries")}
                  />
                )}
            </Flex>
          )}
        </Tabs>
      </div>
    </div>
  );
});

export enum CountrySet {
  DOMESTIC = "Domestic",
  INTERNATIONAL = "International",
}

export const CountrySetLables = {
  [CountrySet.DOMESTIC]: <>Domestic</>,
  [CountrySet.INTERNATIONAL]: <>International</>,
};

export function pricingTypeLabel(input: "percentage" | "fixed") {
  switch (input) {
    case "percentage":
      return "Percentage";
    case "fixed":
      return "Fixed amount";
  }
}

export function pricingRulesConvertTeamToValue(
  pricingRuleSet: PricingRuleSet[] | undefined,
  defaultCountries: string[] = [],
  defaultPercentage?: number,
): PricingRuleSetValue[] {
  let pricingRuleSetReturn: PricingRuleSetValue[] = [];
  if (pricingRuleSet?.length) {
    pricingRuleSetReturn = pricingRuleSet.map((x) => {
      return {
        type: x.type,
        countries: x.countries,
        noCoverage: x.noCoverage || undefined,
        priceBrackets: x.priceBrackets.map((pb) => {
          let value = pb.value;
          if (x.type === "percentage") {
            value = round(pb.value * 100, 3);
          }
          return { value: String(value), pricePoint: String(pb.pricePoint) };
        }),
      };
    });
  } else if (!pricingRuleSetReturn.length) {
    pricingRuleSetReturn.push({
      type: "percentage",
      noCoverage: undefined,
      countries: defaultCountries,
      priceBrackets: [
        {
          value: defaultPercentage
            ? String(round(defaultPercentage * 100, 3))
            : "2",
          pricePoint: "0",
        },
      ],
    });
  }

  return pricingRuleSetReturn;
}

/** Rule sets match first-to-last, so "everywhere else" groups must come after the country groups. */
export function orderCountryGroups<Rule extends { countries: string[] }>(
  pricingRuleSets: Rule[],
): Rule[] {
  const [domestic, ...international] = pricingRuleSets;
  if (!domestic) {
    return pricingRuleSets;
  }
  return [
    domestic,
    ...international.filter((rule) => rule.countries.length),
    ...international.filter((rule) => !rule.countries.length),
  ];
}

export function pricingRulesConvertValueToTeam(
  pricingRuleSets: PricingRuleSetValue[] | undefined,
  countryGroups = false,
) {
  let teamReturn;
  if (pricingRuleSets) {
    const orderedRules = countryGroups
      ? orderCountryGroups(pricingRuleSets)
      : pricingRuleSets;
    teamReturn = orderedRules.map((x) => {
      return {
        type: x.type,
        countries: x.countries,
        ...(x.noCoverage ? { noCoverage: true } : {}),
        priceBrackets: x.priceBrackets.map((pb) => {
          let newValue = Number(pb.value);
          if (x.type === "percentage") {
            newValue = round(newValue / 100.0, 3);
          }
          return { value: newValue, pricePoint: Number(pb.pricePoint) };
        }),
      };
    });

    return teamReturn;
  } else {
    return undefined;
  }
}

export const MinMaxPriceInput = memo(function MinMaxPriceInput({
  minPriceInput,
  maxPriceInput,
  canEditSettings,
  currencyDisplay,
  minPriceFooter,
  maxPriceFooter,
  maxPriceDisabled = false,
}: {
  minPriceInput: Input<string>;
  maxPriceInput: Input<string>;
  canEditSettings: boolean;
  currencyDisplay?: string;
  // Hint rendered under the min or max price field.
  minPriceFooter?: ReactNode;
  maxPriceFooter?: ReactNode;
  // Read-only max price for surfaces where it's derived (PP+).
  maxPriceDisabled?: boolean;
}) {
  return (
    <>
      <div className={classNames(gridCss.span6L, pricingCss.monospace)}>
        <SettingWrapper id="min-price">
          <FormTextInput
            disabled={!canEditSettings}
            input={minPriceInput}
            label="Min price"
            prefix={currencyDisplay}
            type="number"
          />
        </SettingWrapper>
        {minPriceFooter}
      </div>
      <div className={classNames(gridCss.span6L, pricingCss.monospace)}>
        <SettingWrapper id="max-price">
          <FormTextInput
            disabled={!canEditSettings || maxPriceDisabled}
            input={maxPriceInput}
            label="Max price"
            prefix={currencyDisplay}
            type="number"
          />
        </SettingWrapper>
        {maxPriceFooter}
      </div>
    </>
  );
});

export type CoverageMlModelMode =
  | { mode: "edit" }
  | { mode: "restricted"; reason: string };

export function getCoverageMlModelMode({
  email,
  restrictedReason,
}: {
  email: string | undefined;
  restrictedReason: string;
}): CoverageMlModelMode {
  return isAdminCheckoutOptimizationUser(email)
    ? { mode: "edit" as const }
    : { mode: "restricted" as const, reason: restrictedReason };
}

type IntentModelConfigInput = GroupInput<{
  minPriceBiasPercentOfConstraintRange: Input<string>;
  maxPriceBiasPercentOfConstraintRange: Input<string>;
}>;

export const PackageProtectionMlModelSettings = memo(
  function PackageProtectionMlModelSettings({
    coverageMlModelEnabled,
    coverageMlModelId,
    canEditSettings,
    coverageMlModelMode,
    minPriceInput,
    maxPriceInput,
    currencyDisplay,
    intentModelConfig,
    email,
    minPriceFooter,
    maxPriceFooter,
    maxPriceDisabled,
    stagecoachPriceVsConversionWeight,
    stagecoachThompsonSamplingEnabled,
  }: {
    coverageMlModelEnabled: Input<boolean>;
    coverageMlModelId: Input<CoverageMlModelId>;
    canEditSettings: boolean;
    coverageMlModelMode: CoverageMlModelMode;
    minPriceInput?: Input<string>;
    maxPriceInput?: Input<string>;
    minPriceFooter?: ReactNode;
    maxPriceFooter?: ReactNode;
    maxPriceDisabled?: boolean;
    currencyDisplay?: string;
    intentModelConfig?: IntentModelConfigInput;
    email?: string;
    stagecoachPriceVsConversionWeight?: Input<string>;
    stagecoachThompsonSamplingEnabled?: Input<boolean>;
  }) {
    const options = useMemo(() => {
      const base = [...baseMlModelOptions];
      if (isTrustedAdminCheckoutOptimizationUser(email)) {
        base.push({
          id: STAGECOACH_COVERAGE_ML_MODEL_ID,
          type: "text" as const,
          value: STAGECOACH_COVERAGE_ML_MODEL_ID,
          text: "Stagecoach (ML model)",
        });
      }
      return base;
    }, [email]);

    if (
      coverageMlModelMode.mode === "restricted" &&
      !coverageMlModelEnabled.value
    ) {
      return null;
    }

    return (
      <SettingSectionWrapper id="ml-model">
        <div className={classNames(gridCss.span12)}>
          <Flex dir="column" gap="xs" mb="md" w="full">
            <SettingWrapper compact id="use-ml-model">
              <RedoToggle
                description="Enable machine learning models to optimize package protection pricing"
                disabled={
                  !canEditSettings ||
                  (coverageMlModelMode.mode === "restricted" &&
                    coverageMlModelEnabled.value)
                    ? ((coverageMlModelMode.mode === "restricted" &&
                      coverageMlModelEnabled.value
                        ? coverageMlModelMode.reason
                        : undefined) ?? true)
                    : false
                }
                label="Use ML model for pricing"
                setValue={coverageMlModelEnabled.setValue}
                size="sm"
                value={coverageMlModelEnabled.value}
              />
            </SettingWrapper>
            {coverageMlModelEnabled.value && (
              <Flex dir="column" gap="xs" mt="sm">
                <DisabledTooltip
                  disabledMessage={
                    coverageMlModelMode.mode === "restricted" &&
                    coverageMlModelEnabled.value
                      ? coverageMlModelMode.reason
                      : undefined
                  }
                >
                  <SettingWrapper id="ml-model-select">
                    <RedoSingleSelectDropdownInput
                      description="Choose the machine learning model for price optimization"
                      disabled={
                        !canEditSettings ||
                        (coverageMlModelMode.mode === "restricted" &&
                          coverageMlModelEnabled.value)
                      }
                      fullWidth
                      label="ML model"
                      options={options}
                      optionSelected={({ item }) => {
                        coverageMlModelId.setValue(item.value);
                      }}
                      placeholder="Select ML model"
                      selectedItem={options.find(
                        (option) => option.value === coverageMlModelId.value,
                      )}
                      size="sm"
                    />
                  </SettingWrapper>
                </DisabledTooltip>
                {coverageMlModelId.value ===
                  INTENT_V12_28_2025_COVERAGE_ML_MODEL_ID &&
                  intentModelConfig && (
                    <div className={gridCss.grid}>
                      <div className={classNames(gridCss.span6)}>
                        <SettingWrapper id="min-price-bias">
                          <FormTextInput
                            description="Min bias % (-1 to 1)"
                            input={
                              intentModelConfig.inputs
                                .minPriceBiasPercentOfConstraintRange
                            }
                            label="Min price bias %"
                            labelTheme={LabelTheme.THIN}
                            placeholder="0"
                          />
                        </SettingWrapper>
                      </div>
                      <div className={classNames(gridCss.span6)}>
                        <SettingWrapper id="max-price-bias">
                          <FormTextInput
                            description="Max bias % (-1 to 1, default: 0.3)"
                            input={
                              intentModelConfig.inputs
                                .maxPriceBiasPercentOfConstraintRange
                            }
                            label="Max price bias %"
                            labelTheme={LabelTheme.THIN}
                            placeholder="0.3"
                          />
                        </SettingWrapper>
                      </div>
                    </div>
                  )}
                {coverageMlModelId.value === STAGECOACH_COVERAGE_ML_MODEL_ID &&
                  stagecoachPriceVsConversionWeight && (
                    <div className={gridCss.grid}>
                      <div className={classNames(gridCss.span6)}>
                        <SettingWrapper id="conversion-weight">
                          <FormTextInput
                            description="1.0 = maximize revenue, <1 favors conversion, >1 favors margin"
                            input={stagecoachPriceVsConversionWeight}
                            label="Price vs conversion weight (α)"
                            labelTheme={LabelTheme.THIN}
                            placeholder="1.0"
                          />
                        </SettingWrapper>
                      </div>
                      {stagecoachThompsonSamplingEnabled && (
                        <div className={classNames(gridCss.span6)}>
                          <SettingWrapper id="thompson-sampling">
                            <RedoToggle
                              description="Sample from posterior distribution instead of using posterior mean"
                              disabled={!canEditSettings}
                              label="Thompson sampling"
                              setValue={
                                stagecoachThompsonSamplingEnabled.setValue
                              }
                              size="sm"
                              value={stagecoachThompsonSamplingEnabled.value}
                            />
                          </SettingWrapper>
                        </div>
                      )}
                    </div>
                  )}
                {minPriceInput &&
                  maxPriceInput &&
                  coverageMlModelId.value !==
                    INTENT_V12_28_2025_COVERAGE_ML_MODEL_ID && (
                    <div className={gridCss.grid}>
                      <MinMaxPriceInput
                        canEditSettings={canEditSettings}
                        currencyDisplay={currencyDisplay}
                        maxPriceDisabled={maxPriceDisabled}
                        maxPriceFooter={maxPriceFooter}
                        maxPriceInput={maxPriceInput}
                        minPriceFooter={minPriceFooter}
                        minPriceInput={minPriceInput}
                      />
                    </div>
                  )}
              </Flex>
            )}
          </Flex>
        </div>
      </SettingSectionWrapper>
    );
  },
);

const baseMlModelOptions: TextRedoListItem<CoverageMlModelId>[] = [
  {
    id: INTENT_V12_28_2025_COVERAGE_ML_MODEL_ID,
    type: "text" as const,
    value: INTENT_V12_28_2025_COVERAGE_ML_MODEL_ID,
    text: "Intent v12_28_2025",
  },
];
